import { useMemo, useState, useRef, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, ArrowUpDown, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ColumnDef<T> {
  key: string;
  label: string;
  /** Largeur Tailwind ou px : ex "w-48", "min-w-[120px]" */
  className?: string;
  /** Largeur fixe en px (style inline — fiable avec table-fixed). */
  width?: number;
  /** Rendu d'une cellule. */
  render: (row: T) => React.ReactNode;
  /** Comparateur pour tri (renvoie l'élément triable — string/number/date). */
  sortValue?: (row: T) => string | number | Date | null | undefined;
  /** Alignement */
  align?: "left" | "right" | "center";
  /** Compacte (cell padding réduit). */
  compact?: boolean;
}

export interface SortState {
  key: string;
  dir: "asc" | "desc";
}

export interface DataTableProps<T> {
  rows: T[];
  columns: ColumnDef<T>[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Champ de recherche libre (filtre client). */
  searchable?: boolean;
  /** Fonction renvoyant un texte concaténé sur lequel chercher. */
  searchAccessor?: (row: T) => string;
  searchPlaceholder?: string;
  /** Tri par défaut */
  defaultSort?: SortState;
  /** Sort contrôlé (le parent gère le state). Quand fourni, le DataTable
   * n'applique PAS son tri interne — il rend rows tel quel. Utile quand
   * le parent veut trier la liste complète AVANT pagination. */
  sort?: SortState | null;
  onSortChange?: (sort: SortState | null) => void;
  /** Affiché si rows est vide */
  emptyLabel?: string;
  /** Affiché en haut à droite (boutons d'action). */
  toolbar?: React.ReactNode;
  /** Indique que les données sont en chargement. */
  isLoading?: boolean;
  /** Striping (lignes alternées) */
  striped?: boolean;
  /** Densité par défaut */
  compact?: boolean;
  /** Largeur minimale de la table en px. Si > conteneur → scroll horizontal
   *  (les colonnes gardent leur largeur au lieu de rétrécir). */
  minWidth?: number;
  /** Hauteur max du conteneur scrollable (valeur CSS, ex. "calc(100vh - 16rem)").
   *  Quand fournie : le corps scrolle DANS le conteneur (overflow auto) et le
   *  header devient collant → la scrollbar horizontale reste visible sans devoir
   *  descendre tout en bas de toutes les lignes. Sinon : comportement historique
   *  (overflow-x seul, scrollbar horizontale en bas de la table entière). */
  maxHeight?: string;
  /** Variante auto de `maxHeight` : mesure la position du conteneur dans le
   *  viewport et le dimensionne pour remplir jusqu'en bas de l'écran (recalc au
   *  resize). Garantit que la scrollbar horizontale reste visible sans dépendre
   *  d'un offset fixe (le chrome au-dessus de la table varie selon la page). */
  fillViewport?: boolean;
}

/**
 * Table générique : tri par colonne (chevron), recherche libre, états vides,
 * separators verticaux entre colonnes pour matcher la V1.
 *
 * Volontairement minimaliste — picker de colonnes et resize laissés aux pages
 * qui en ont besoin (et seront migrés au cas par cas).
 */
export function DataTable<T>({
  rows,
  columns,
  rowKey,
  onRowClick,
  searchable = true,
  searchAccessor,
  searchPlaceholder,
  defaultSort,
  sort: controlledSort,
  onSortChange,
  emptyLabel,
  toolbar,
  isLoading,
  striped = true,
  compact,
  minWidth,
  maxHeight,
  fillViewport,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const isSortControlled = controlledSort !== undefined;
  const [internalSort, setInternalSort] = useState<SortState | null>(
    defaultSort ?? null,
  );
  const sort = isSortControlled ? controlledSort : internalSort;
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s || !searchAccessor) return rows;
    return rows.filter((r) =>
      searchAccessor(r).toLowerCase().includes(s),
    );
  }, [rows, search, searchAccessor]);

  const sorted = useMemo(() => {
    // En mode contrôlé : le parent a déjà trié, on rend tel quel.
    if (isSortControlled) return filtered;
    if (!sort) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return filtered;
    const cmp = (a: T, b: T) => {
      const va = col.sortValue!(a);
      const vb = col.sortValue!(b);
      const empty = (v: typeof va) => v === null || v === undefined || v === "";
      if (empty(va) && empty(vb)) return 0;
      if (empty(va)) return 1;
      if (empty(vb)) return -1;
      if (va instanceof Date && vb instanceof Date) {
        return va.getTime() - vb.getTime();
      }
      if (typeof va === "number" && typeof vb === "number") {
        return va - vb;
      }
      return String(va).localeCompare(String(vb), "fr", { numeric: true });
    };
    const copy = [...filtered];
    copy.sort((a, b) => (sort.dir === "asc" ? cmp(a, b) : cmp(b, a)));
    return copy;
  }, [filtered, sort, columns, isSortControlled]);

  const toggleSort = (key: string) => {
    const next: SortState | null =
      sort?.key !== key ? { key, dir: "asc" }
        : sort.dir === "asc" ? { key, dir: "desc" }
          : null;
    if (isSortControlled) {
      onSortChange?.(next);
    } else {
      setInternalSort(next);
    }
  };

  const cellPad = compact ? "px-2 py-1.5" : "px-3 py-2.5";

  // Mode fillViewport : on mesure la distance haut-conteneur → bord viewport et
  // on borne la hauteur pour que la scrollbar horizontale tienne à l'écran.
  // Recalcul au resize (le chrome au-dessus dépend de la largeur). Plancher
  // 240px pour rester utilisable sur petit écran.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoMaxH, setAutoMaxH] = useState<string | undefined>(undefined);
  useLayoutEffect(() => {
    if (!fillViewport) return;
    const recompute = () => {
      const el = scrollRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const h = Math.max(240, window.innerHeight - top - 24);
      setAutoMaxH(`${Math.round(h)}px`);
    };
    recompute();
    window.addEventListener("resize", recompute);
    return () => window.removeEventListener("resize", recompute);
  }, [fillViewport]);

  const contained = !!maxHeight || fillViewport;
  const effectiveMaxH = maxHeight ?? autoMaxH;

  // Indicateur de scroll horizontal restant (dégradé sur les bords) — sans
  // ça, rien ne signale qu'un tableau large peut défiler vers la droite sur
  // un Mac où les barres de défilement système sont masquées par défaut
  // (souris sans trackpad : pas de geste de scroll latéral évident non plus)
  // — signalé Thibaud 2026-08-20. Indépendant des réglages système,
  // fonctionne quelle que soit la visibilité de la scrollbar OS.
  const [edgeFade, setEdgeFade] = useState({ left: false, right: false });
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      setEdgeFade({
        left: el.scrollLeft > 2,
        right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2,
      });
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);

    // Molette verticale → scroll horizontal quand c'est la SEULE direction
    // possible (souris sans trackpad ni molette latérale : aucun autre
    // moyen de faire défiler un tableau large — signalé Thibaud 2026-08-20,
    // "ma souris n'a pas de défilement horizontal"). Ne s'active QUE si le
    // conteneur n'a pas de scroll vertical propre à ce moment (mode
    // fillViewport/maxHeight avec beaucoup de lignes garde son scroll
    // vertical normal) et qu'il a du scroll horizontal disponible — sinon
    // on casserait le scroll vertical de la page ou des tableaux "contenus".
    const onWheel = (e: WheelEvent) => {
      if (e.deltaX !== 0) return; // trackpad/molette horizontale déjà native
      const hasVerticalOverflow = el.scrollHeight > el.clientHeight;
      const hasHorizontalOverflow = el.scrollWidth > el.clientWidth;
      if (hasVerticalOverflow || !hasHorizontalOverflow) return;
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    };
    el.addEventListener("wheel", onWheel, { passive: false });

    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
      el.removeEventListener("wheel", onWheel);
    };
  }, [sorted.length, columns.length, minWidth]);

  return (
    <div className="flex flex-col gap-2">
      {(searchable || toolbar) && (
        <div className="flex items-center gap-2">
          {searchable && (
            <div className="relative flex-1 max-w-md">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder ?? t("common.search")}
                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-border bg-surface outline-none shadow-none"
              />
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">{toolbar}</div>
        </div>
      )}

      <div className="relative">
      <div
        ref={scrollRef}
        className={cn(
          "rounded-md border border-border bg-surface",
          contained ? "overflow-auto" : "overflow-x-auto",
        )}
        style={effectiveMaxH ? { maxHeight: effectiveMaxH } : undefined}
      >
        <table className="w-full text-sm table-fixed" style={minWidth ? { minWidth } : undefined}>
          <thead className={cn("bg-background border-b border-border", contained && "sticky top-0 z-10")}>
            <tr className="divide-x divide-border">
              {columns.map((col) => {
                const isActive = sort?.key === col.key;
                const sortable = !!col.sortValue;
                return (
                  <th
                    key={col.key}
                    style={col.width ? { width: col.width } : undefined}
                    className={cn(
                      "text-[10px] uppercase tracking-wider text-text-muted font-semibold text-left",
                      cellPad,
                      col.className,
                      col.align === "right" && "text-right",
                      col.align === "center" && "text-center",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => sortable && toggleSort(col.key)}
                      className={cn(
                        "inline-flex items-center gap-1",
                        sortable && "cursor-pointer hover:text-text-primary",
                      )}
                      disabled={!sortable}
                    >
                      <span>{col.label}</span>
                      {sortable &&
                        (isActive ? (
                          sort?.dir === "asc" ? (
                            <ChevronUp size={12} className="text-accent" />
                          ) : (
                            <ChevronDown size={12} className="text-accent" />
                          )
                        ) : (
                          <ArrowUpDown size={10} className="opacity-40" />
                        ))}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center text-text-muted py-12"
                >
                  {t("common.loading")}
                </td>
              </tr>
            ) : sorted.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center text-text-muted py-12"
                >
                  {emptyLabel ?? t("v2.dataTable.empty")}
                </td>
              </tr>
            ) : (
              sorted.map((row, idx) => (
                <tr
                  key={rowKey(row)}
                  onClick={() => onRowClick?.(row)}
                  className={cn(
                    "divide-x divide-border border-b border-border",
                    striped && idx % 2 === 1 && "bg-background/50",
                    onRowClick && "cursor-pointer hover:bg-accent-light/40",
                  )}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      style={col.width ? { width: col.width } : undefined}
                      className={cn(
                        cellPad,
                        col.className,
                        col.align === "right" && "text-right",
                        col.align === "center" && "text-center",
                      )}
                    >
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {edgeFade.left && (
        <>
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 rounded-l-md bg-gradient-to-r from-surface to-transparent" />
          {/* Bouton explicite en plus de la molette redirigée : garantit le
              scroll même quand rien (souris, trackpad, molette) ne le
              déclenche sur la machine (signalé Thibaud 2026-08-20 : "ca ne
              fonctionne toujours pas... un petit icône flèche droite ?"). */}
          <button
            type="button"
            onClick={() => scrollRef.current?.scrollBy({ left: -240, behavior: "smooth" })}
            aria-label={t("v2.dataTable.scrollLeft")}
            className="absolute left-1 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-7 h-7 rounded-full bg-surface border border-border shadow-md text-text-secondary hover:text-accent hover:border-accent cursor-pointer outline-none shadow-none"
          >
            <ChevronLeft size={16} />
          </button>
        </>
      )}
      {edgeFade.right && (
        <>
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 rounded-r-md bg-gradient-to-l from-surface to-transparent" />
          <button
            type="button"
            onClick={() => scrollRef.current?.scrollBy({ left: 240, behavior: "smooth" })}
            aria-label={t("v2.dataTable.scrollRight")}
            className="absolute right-1 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-7 h-7 rounded-full bg-surface border border-border shadow-md text-text-secondary hover:text-accent hover:border-accent cursor-pointer outline-none shadow-none"
          >
            <ChevronRight size={16} />
          </button>
        </>
      )}
      </div>

      <div className="text-xs text-text-muted">
        {t("v2.dataTable.rowCount", { count: sorted.length })}
        {search && rows.length !== sorted.length && (
          <span> {t("v2.dataTable.filteredFrom", { count: rows.length })}</span>
        )}
      </div>
    </div>
  );
}
